import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart as CartIcon, X, Plus, Minus, Trash2, ShoppingBag, Gift } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { useCart } from '../context/CartContext';
import { CartShippingTeaser } from './cart/CartShippingTeaser';
import { CartMinOrderNote } from './cart/CartMinOrderNote';
import { CartGiftNudge } from './cart/CartGiftNudge';

export const CartButton = () => {
  const { totalItems } = useCart();
  const [open, setOpen] = useState(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          className="relative h-11 w-11 flex items-center justify-center rounded-full text-shrimp-text hover:text-shrimp-green active:bg-shrimp-beige transition-colors"
          data-testid="cart-button"
          aria-label="Shopping cart"
        >
          <CartIcon className="w-5 h-5" />
          {totalItems > 0 && (
            <span className="absolute top-1 right-1 bg-shrimp-orange text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">
              {totalItems}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md bg-surface h-[100dvh] flex flex-col pb-[max(1.5rem,env(safe-area-inset-bottom))]">
        <SheetHeader>
          <SheetTitle className="font-display text-xl flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-shrimp-green" />
            Reservation Basket
          </SheetTitle>
        </SheetHeader>
        <CartContents onClose={() => setOpen(false)} />
      </SheetContent>
    </Sheet>
  );
};

const CartContents = ({ onClose }) => {
  const { items, updateQuantity, removeItem, totalPrice, totalItems, totalBonusItems, eligibleBonusQuantity, clearCart } = useCart();

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center">
        <CartIcon className="w-16 h-16 text-shrimp-border mb-4" />
        <p className="text-shrimp-text mb-2">Your basket is empty</p>
        <p className="text-sm text-shrimp-text/70">Add some shrimp to get started!</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex-1 min-h-0 overflow-y-auto py-4 space-y-4">
        {items.map((item) => (
          <div
            key={item.shrimp_id}
            className="flex gap-3 p-3 bg-shrimp-bg rounded-lg"
            data-testid={`cart-item-${item.shrimp_id}`}
          >
            {item.image && (
              <img loading="lazy"
                src={item.image}
                alt={item.shrimp_name}
                className="w-16 h-16 object-cover rounded-lg"
              />
            )}
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-shrimp-dark text-sm truncate">
                {item.shrimp_name}
              </h4>
              <p className="text-xs text-shrimp-text">
                €{item.unit_price.toFixed(2)} {item.product_type === 'jar' ? '' : item.product_type === 'simple' ? 'per item' : 'per item'}
              </p>
              {item.discount_percent > 0 && (
                <p className="text-xs text-shrimp-orange">
                  🏷️ {item.discount_name || 'Discount'} (-{item.discount_percent}%)
                </p>
              )}
              
              {/* Quantity Input */}
              <div className="flex items-center gap-2 mt-2">
                <button
                  onClick={() => updateQuantity(item.cart_item_id || item.shrimp_id, item.quantity - 1)}
                  className="w-9 h-9 rounded-lg bg-surface border border-shrimp-border flex items-center justify-center hover:bg-shrimp-beige active:scale-95 transition-transform"
                  aria-label="Decrease quantity"
                >
                  <Minus className="w-3.5 h-3.5" />
                </button>
                <Input
                  type="number"
                  min="1"
                  value={item.quantity}
                  onChange={(e) => updateQuantity(item.cart_item_id || item.shrimp_id, parseInt(e.target.value) || 1)}
                  className="w-14 h-9 text-center text-sm bg-surface border-shrimp-border [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <button
                  onClick={() => updateQuantity(item.cart_item_id || item.shrimp_id, item.quantity + 1)}
                  className="w-9 h-9 rounded-lg bg-surface border border-shrimp-border flex items-center justify-center hover:bg-shrimp-beige active:scale-95 transition-transform"
                  aria-label="Increase quantity"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Bonus Indicator */}
              {item.bonus_quantity > 0 && (
                <div className="flex items-center gap-1 mt-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full w-fit">
                  <Gift className="w-3 h-3" />
                  +{item.bonus_quantity} FREE!
                </div>
              )}
            </div>
            <div className="flex flex-col items-end justify-between">
              <button
                onClick={() => removeItem(item.cart_item_id || item.shrimp_id)}
                className="text-red-400 hover:text-red-500 p-2 -mr-2 -mt-1 rounded-lg hover:bg-red-50"
                aria-label={`Remove ${item.shrimp_name}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <span className="font-medium text-shrimp-dark">
                €{item.total_price.toFixed(2)}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="border-t border-shrimp-border pt-4 space-y-4">
        {/* Summary */}
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-shrimp-text">Items ordered</span>
            <span className="font-medium">{totalItems}</span>
          </div>
          {totalBonusItems > 0 && (
            <div className="flex justify-between text-green-600">
              <span className="flex items-center gap-1">
                <Gift className="w-4 h-4" /> Free bonus shrimp
              </span>
              <span className="font-medium">+{totalBonusItems}</span>
            </div>
          )}
          <div className="flex justify-between font-bold text-lg border-t pt-2">
            <span className="text-shrimp-text">Total ({totalItems + totalBonusItems} {(totalItems + totalBonusItems) === 1 ? 'item' : 'items'})</span>
            <span className="text-shrimp-green">€{totalPrice.toFixed(2)}</span>
          </div>
        </div>

        {/* Bonus Hint — only when the cart has bonus-eligible individual shrimp */}
        {eligibleBonusQuantity > 0 && eligibleBonusQuantity < 10 && (
          <div className="bg-yellow-50 text-yellow-700 text-xs p-2 rounded-lg text-center">
            🎁 Add {10 - eligibleBonusQuantity} more shrimp to get <strong>+1 FREE shrimp!</strong>
          </div>
        )}
        {eligibleBonusQuantity >= 10 && eligibleBonusQuantity < 20 && (
          <div className="bg-yellow-50 text-yellow-700 text-xs p-2 rounded-lg text-center">
            🎁 Add {20 - eligibleBonusQuantity} more shrimp to get <strong>+3 FREE shrimp!</strong> (instead of +1)
          </div>
        )}

        {/* Shipping rate teaser — food-only vs live-animal rate card */}
        <CartShippingTeaser items={items} totalPrice={totalPrice} />
        <CartMinOrderNote items={items} totalPrice={totalPrice} />
        <CartGiftNudge totalPrice={totalPrice} />

        <p className="text-xs text-shrimp-text text-center">
          💵 Payment due on pickup
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            onClick={clearCart}
            className="border-shrimp-border text-shrimp-text h-11"
          >
            Clear All
          </Button>
          <Link to="/checkout" onClick={onClose}>
            <Button className="w-full bg-shrimp-green hover:bg-shrimp-text h-11 text-base" data-testid="cart-checkout-button">
              Checkout
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CartButton;
